import {UserType} from "../types/types";
import {BaseThunkType, InferActionsTypes} from "./redux-store";
import {Dispatch} from "redux";
import {usersAPI} from "../api/usersAPI";
import {APIResponseType} from "../api/api";

let initialState = {
    users: [] as Array<UserType>,
    currentPage: 1,
    pageSize: 16,
    totalCount: 0,
    isFetching: false,
    followingInProgress: [] as Array<number>, // array of users ids
    filter: {
        term: '',
        friend: null as null | boolean
    },
}

export const usersReducer = (state = initialState, action: ActionsTypes): InitialStateType => {
    switch (action.type) {
        case "users/FOLLOW":
            return {
                ...state,
                users: state.users.map(u => {
                    if (u.id === action.userId) {
                        return {...u, followed: true}
                    }
                    return u
                })
            }
        case "users/UNFOLLOW":
            return {
                ...state,
                users: state.users.map(u => {
                    if (u.id === action.userId) {
                        return {...u, followed: false}
                    }
                    return u
                })
            }
        case "users/SET_USERS":
            return {
                ...state, users: action.users
            }
        case "users/SET_TOTAL":
            return {
                ...state, totalCount: action.totalCount
            }
        case "users/SET_CURRENT_PAGE":
            return {
                ...state, currentPage: action.currentPage
            }
        case "users/SET_FILTER":
            return {
                ...state, filter: action.payload
            }
        case "users/SHOW_PRELOADER":
            return {
                ...state, isFetching: action.preloaderToggle
            }
        case "users/TOGGLE_FOLLOWING_IN_PROGRESS":
            return {
                ...state,
                followingInProgress: action.isFetching
                    ? [...state.followingInProgress, action.userId]
                    : state.followingInProgress.filter((id) => id != action.userId)
            }
        default:
            return state
    }
}

export const actions = {
    followSuccess: (userId: number) => ({type: "users/FOLLOW", userId} as const),
    unfollowSuccess: (userId: number) => ({type: "users/UNFOLLOW", userId} as const),
    setUsers: (users: Array<UserType>) => ({type: "users/SET_USERS", users} as const),
    setTotal: (totalCount: number) => ({type: "users/SET_TOTAL", totalCount} as const),
    setCurrentPage: (currentPage: number) => ({
        type: "users/SET_CURRENT_PAGE",
        currentPage: currentPage
    }) as const,
    setFilter: (filter: FilterType) => ({type: "users/SET_FILTER", payload: {...filter}}) as const,
    showPreloader: (preloaderToggle: boolean) => ({
        type: "users/SHOW_PRELOADER",
        preloaderToggle
    } as const),
    toggleFollowingInProgress: (isFetching: boolean, userId: number) => ({
        type: "users/TOGGLE_FOLLOWING_IN_PROGRESS",
        isFetching,
        userId
    } as const)
}

export const getUsers = (currentPage: number, pageSize: number, filter: FilterType):ThunkType => {
    return async (dispatch) => {
        dispatch(actions.showPreloader(true))
        dispatch(actions.setCurrentPage(currentPage))
        dispatch(actions.setFilter(filter))
        let data = await usersAPI.getUsers(currentPage, pageSize, filter.term, filter.friend)

        dispatch(actions.showPreloader(false))
        dispatch(actions.setUsers(data.items))
        dispatch(actions.setTotal(data.totalCount))
    };
}

const _followUnfollowFlow = async (dispatch: Dispatch<ActionsTypes>,
                                   userId: number,
                                   apiMethod: (userId: number) => Promise<APIResponseType>,
                                   actionCreator: (userId: number) => ActionsTypes) => {
    dispatch(actions.toggleFollowingInProgress(true, userId))
    let data = await apiMethod(userId)

    if (data.resultCode == 0) {
        dispatch(actionCreator(userId))
    }
    dispatch(actions.toggleFollowingInProgress(false, userId))

}

export const unfollow = (userId: number):ThunkType => {
    return async (dispatch) => {
        await _followUnfollowFlow(dispatch, userId, usersAPI.unfollow.bind(usersAPI), actions.unfollowSuccess)
    }
}

export const follow = (userId: number):ThunkType => {
    return async (dispatch) => {
        await _followUnfollowFlow(dispatch, userId, usersAPI.follow.bind(usersAPI), actions.followSuccess)
    }
}

export default usersReducer

export type InitialStateType = typeof initialState
export type FilterType = typeof initialState.filter
type ActionsTypes = InferActionsTypes<typeof actions>
type ThunkType = BaseThunkType<ActionsTypes>
